export type QueryTypeKey =
  | "instructions"
  | "diagnosis"
  | "product_information"
  | "finding_location"
  | null;

export interface RagImageItem {
  path: string;
  metadata?: { page?: string | number };
}

/** Playback URL in `path`; `name` is the clean filename for UI labels. */
export interface RagVideoItem {
  path: string;
  name: string;
}

export interface RagSource {
  type?: string;
  path?: string;
  pdf_name?: string;
  page?: string | number;
}

export interface RagAlert {
  level: string;
  text: string;
}

/**
 * DF-1 / DF-2 — Ranked Source Presentation.
 * Nested keys arrive snake_case from the worker (preserved verbatim through
 * Instrakt), so these mirror the worker contract exactly.
 */
export interface RagShowDetails {
  target_pdf?: string;
}

export interface RagPrimarySource {
  pdf_name: string;
  source_label?: string;
  document_category?: string;
  pages?: number[];
  relevance?: number;
  relevance_band?: string;
  kind?: string; // "document" | "ai_answer"
  document_content?: string;
  answer?: string;
  show_details?: RagShowDetails;
}

export interface RagAlternativeSource {
  pdf_name: string;
  source_label?: string;
  document_category?: string;
  pages?: number[];
  relevance?: number;
  relevance_band?: string;
  kind?: string; // "document" | "ai_summary"
  summary?: string;
  document_content?: string;
  show_details?: RagShowDetails;
}

export interface RagAiEnhancement {
  text: string;
  advisory?: boolean;
}

export interface RagUserMessage {
  id: string;
  role: "user";
  content: string;
}

export interface RagAssistantMessage {
  id: string;
  role: "assistant";
  answer: string;
  images: RagImageItem[];
  videos: RagVideoItem[];
  sources: RagSource[];
  options: string[];
  inlineImages: Record<string, RagImageItem[]>;
  pageTables: Record<string, string[]>;
  pageAlerts: Record<string, RagAlert[]>;
  relatedQuestions: string[];
  alternativePdfs: string[];
  responseType: string;
  queryType: QueryTypeKey;
  // DF-1 / DF-2 — Ranked Source Presentation (optional; absent on plain answers)
  primarySource: RagPrimarySource | null;
  alternativeSources: RagAlternativeSource[];
  aiEnhancement: RagAiEnhancement | null;
  refusalReason?: string | null;
}

export type RagMessage = RagUserMessage | RagAssistantMessage;

export function isAssistantMessage(m: RagMessage): m is RagAssistantMessage {
  return m.role === "assistant";
}
